import { documentsConfiguration, deviceHelper } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getOpenLocalizationsForExtension, OpenLocalizations } from "client_legacy/Documents/openLocalizations";
import { getExtension } from "client_legacy/Documents/path";
import {
	OneNoteNotebookViewModel,
	FileViewModel,
	DocumentItemType,
	RecentDocumentItemViewModel
} from "client/Api/Documents/SharePoint/common";
import { ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION } from "client_legacy/Documents/fileExtensions";

interface DocumentOpenPossibilities {
	canBeOpenedOnline: boolean;
	canBeOpenedInSharePointViewer: boolean;
	canBeOpenedInTheBrowser: boolean;
	canBeOpenedLocally: boolean;
	canBeOpenedInImagePreviewDialog: boolean;
	canBeOpenedAsExternalUrl: boolean;
}

function calculate(document: FileViewModel | OneNoteNotebookViewModel | RecentDocumentItemViewModel): DocumentOpenPossibilities {
	switch (document.itemType) {
		case DocumentItemType.OneNoteNotebook:
			return calculateForExtension(ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION);
		case DocumentItemType.File:
			return calculateForFileName(document.name);
		default:
			return unknownDocument(document);
	}
}

function calculateForFileName(filename: string): DocumentOpenPossibilities {
	const extension: string = getExtension(filename);
	return calculateForExtension(extension);
}

function calculateForExtension(extension: string): DocumentOpenPossibilities {
	const {
		sharePoint
	} = documentsConfiguration();

	switch (extension.toLowerCase()) {
		case ".doc":
		case ".docx":
		case ".dotx":
		case ".docm":
		case ".dotm":
		case ".csv":
		case ".xls":
		case ".xlsb":
		case ".xlsx":
		case ".xlsm":
		case ".potx":
		case ".pptx":
		case ".ppt":
		case ".pptm":
			return {
				canBeOpenedOnline: true,
				canBeOpenedInSharePointViewer: false,
				canBeOpenedInTheBrowser: false,
				canBeOpenedLocally: allowToOpenInLocalApp(),
				canBeOpenedInImagePreviewDialog: false,
				canBeOpenedAsExternalUrl: false
			};
		case ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION:
			return {
				canBeOpenedOnline: sharePoint.wopi.isOneNoteSupported,
				canBeOpenedInSharePointViewer: false,
				canBeOpenedInTheBrowser: false,
				canBeOpenedLocally: allowToOpenInLocalApp(),
				canBeOpenedInImagePreviewDialog: false,
				canBeOpenedAsExternalUrl: false
			};
		case ".vsd":
		case ".vsdx":
			return {
				canBeOpenedOnline: sharePoint.wopi.isVisioSupported,
				canBeOpenedInSharePointViewer: false,
				canBeOpenedInTheBrowser: false,
				canBeOpenedLocally: allowToOpenInLocalApp(),
				canBeOpenedInImagePreviewDialog: false,
				canBeOpenedAsExternalUrl: false
			};
		case ".png":
		case ".jpg":
		case ".jpeg":
		case ".gif":
		case ".bmp":
			return {
				canBeOpenedOnline: false,
				canBeOpenedInSharePointViewer: false,
				canBeOpenedInTheBrowser: false,
				canBeOpenedLocally: false,
				canBeOpenedInImagePreviewDialog: true,
				canBeOpenedAsExternalUrl: false
			};
		case ".wopitest":
			return {
				canBeOpenedOnline: true,
				canBeOpenedInSharePointViewer: false,
				canBeOpenedInTheBrowser: false,
				canBeOpenedLocally: false,
				canBeOpenedInImagePreviewDialog: false,
				canBeOpenedAsExternalUrl: false
			};
		case ".pdf":
		case ".txt":
			return {
				canBeOpenedOnline: false,
				canBeOpenedInSharePointViewer: sharePoint.isSharePointViewerEnabled,
				canBeOpenedInTheBrowser: !sharePoint.isSharePointViewerEnabled,
				canBeOpenedLocally: false,
				canBeOpenedInImagePreviewDialog: false,
				canBeOpenedAsExternalUrl: false
			};
		case ".url":
			return {
				canBeOpenedOnline: false,
				canBeOpenedInSharePointViewer: false,
				canBeOpenedInTheBrowser: false,
				canBeOpenedLocally: false,
				canBeOpenedInImagePreviewDialog: false,
				canBeOpenedAsExternalUrl: true
			};
		default:
			return {
				canBeOpenedOnline: false,
				canBeOpenedInSharePointViewer: false,
				canBeOpenedInTheBrowser: true,
				canBeOpenedLocally: false,
				canBeOpenedInImagePreviewDialog: false,
				canBeOpenedAsExternalUrl: false
			};
	}
}

function unknownDocument(document: never): never {
	throw new Error(`Unknown document '${JSON.stringify(document)}'`);
}

function allowToOpenInLocalApp() {
	return documentsConfiguration().sharePoint.localApp.isEnabled && deviceHelper.localApp.isSupported();
}

function getOpenLocalizations(document: FileViewModel | OneNoteNotebookViewModel | RecentDocumentItemViewModel): OpenLocalizations {
	let extension: string;
	switch (document.itemType) {
		case DocumentItemType.OneNoteNotebook:
			extension = ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION;
			break;
		case DocumentItemType.File:
			extension = getExtension(document.name);
			break;
		default:
			return unknownDocument(document);
	}

	return getOpenLocalizationsForExtension(extension);
}

export {
	calculate,
	calculateForFileName,
	calculateForExtension,
	type DocumentOpenPossibilities,
	allowToOpenInLocalApp,
	getOpenLocalizations,
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
};
